/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Renderer2 } from '@angular/core';
import { isNotNullOrUndefined } from 'in-time-core';

export interface TagManagerEntry { tagManagerId: string, elementId: string, dataLayerName: string }

const TAG_MANAGER_ID_REGEX: RegExp = /^GTM-[A-Z0-9]{4,10}$/;

export class GtmHelper {
  public static isValidTagManagerId(tagManagerId: string): boolean {
    return TAG_MANAGER_ID_REGEX.test(tagManagerId);
  }

  public static logEventToGtmTracker({
    analyticsEventName,
    dataLayerName,
    ecommerce = undefined,
    extraData = undefined,
  }: {
    analyticsEventName: string,
    dataLayerName: string,
    ecommerce?: object,
    extraData?: object,
  }): void {
    const requestObject: object = { event: analyticsEventName };

    if(isNotNullOrUndefined(ecommerce)) {
      Object.assign(requestObject, { 'ecommerce': ecommerce });
      // Google is so weird
      (window as any)[dataLayerName].push({ ecommerce: null });
    }

    if(isNotNullOrUndefined(extraData)) {
      Object.assign(requestObject, extraData);
    }

    (window as any)[dataLayerName].push(requestObject);
  }

  public static grantUpdatedConsentToGtmTracker(dataLayerName: string, granted: boolean): void {
    function gtag(
      p0: 'consent',
      p1: 'update',
      p2: { ad_storage: string; ad_user_data: string; ad_personalization: string; analytics_storage: string; }
    ) {
      // eslint-disable-next-line prefer-rest-params
      (window as any)[dataLayerName].push(arguments);
    }

    const consent = granted ? 'granted' : 'denied';
    gtag('consent', 'update', {
      'ad_storage': consent,
      'ad_user_data': consent,
      'ad_personalization': consent,
      'analytics_storage': consent,
    });
  }

  public static getGoogleTagManagerCode(tagManagerId: string, dataLayerName: string, elementId: string, hasConsent: boolean): string {
    const defaultConsent = hasConsent ? 'granted' : 'denied';
    const gtagName = `grantDefaultConsentTo${tagManagerId.substring(4)}`;

    return `
    window['${dataLayerName}'] = window['${dataLayerName}'] || [];
    function ${gtagName}(){ window['${dataLayerName}'].push(arguments); }
  
    ${gtagName}('consent', 'default', {
      'ad_storage': '${defaultConsent}',
      'ad_user_data': '${defaultConsent}',
      'ad_personalization': '${defaultConsent}',
      'analytics_storage': '${defaultConsent}',
    });
  
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.id='${elementId}';j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','${dataLayerName}','${tagManagerId}');`;
  }

  public static getGoogleTagManagerIframeCode(tagManagerId: string): string {
    return `<iframe src="https://www.googletagmanager.com/ns.html?id=${tagManagerId}"
  height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  }

  public static createGoogleTagManager({
    document: _document,
    renderer,
    tagManagerId,
    hasConsent,
    useDefaultDataLayer = false,
  }: {
    document: Document,
    renderer: Renderer2,
    tagManagerId: string,
    hasConsent: boolean,
    useDefaultDataLayer?: boolean
  }): { elementId: string, dataLayerName: string } {
    const tagId = tagManagerId.substring(4);
    const elementId = `gtm-elem-${tagId}`;
    const dataLayerName = useDefaultDataLayer ? 'dataLayer' : `dataLayer${tagId}`;

    const iframeNoScriptElement = renderer.createElement('noscript');
    renderer.setAttribute(iframeNoScriptElement, 'id', `iframe-${elementId}`);
    renderer.setProperty(iframeNoScriptElement, 'innerHTML', GtmHelper.getGoogleTagManagerIframeCode(tagManagerId));
    renderer.appendChild(_document.body, iframeNoScriptElement);

    const loaderScriptElement = renderer.createElement('script');

    renderer.setAttribute(loaderScriptElement, 'id', `loader-${elementId}`);
    renderer.setAttribute(loaderScriptElement, 'type', 'text/javascript');
    renderer.setProperty(loaderScriptElement, 'innerHTML', GtmHelper.getGoogleTagManagerCode(tagManagerId, dataLayerName, elementId, hasConsent));
    renderer.appendChild(_document.head, loaderScriptElement);

    return { elementId, dataLayerName };
  }

  public static destroyGoogleTagManager(
    document: Document,
    elementId: string,
  ): void {
    const iframe = document.getElementById(`iframe-${elementId}`);
    if(iframe != null) {
      iframe.remove();
    }

    const loader = document.getElementById(`loader-${elementId}`);
    if(loader != null) {
      loader.remove();
    }

    const tagManager = document.getElementById(elementId);
    if(tagManager != null) {
      tagManager.remove();
    }
  }
}
